import { Navigate, Route, Routes, useParams, useSearchParams } from 'react-router-dom';
import { TooltipProvider } from '@/components/ui/Tooltip';
import { useAuth } from '@/app/AuthProvider';
import { RequireAuth } from '@/app/RequireAuth';
import { canRead, type Role } from '@/lib/permissions';
import { RequireMfa } from '@/app/RequireMfa';
import { RequireEulaAcceptance } from '@/app/RequireEulaAcceptance';
import { AppShell } from '@/components/layout/AppShell';
import { SignInPage } from '@/pages/auth/SignInPage';
import { AuthCallbackPage } from '@/pages/auth/CallbackPage';
import { PendingPage } from '@/pages/auth/PendingPage';
import { InactivePage } from '@/pages/auth/InactivePage';
import { MfaSetupPage } from '@/pages/auth/MfaSetupPage';
import { MfaChallengePage } from '@/pages/auth/MfaChallengePage';
import { MfaSettingsPage } from '@/pages/auth/MfaSettingsPage';
import { SettingsPage } from '@/pages/settings/SettingsPage';
import { ForbiddenPage } from '@/pages/ForbiddenPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { HomePage } from '@/pages/HomePage';
import { EulaPage } from '@/pages/legal/EulaPage';
import { EulaAcceptPage } from '@/pages/legal/EulaAcceptPage';
import { PrivacyPolicyPage } from '@/pages/legal/PrivacyPolicyPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { NotificationsPage } from '@/pages/NotificationsPage';
import { CalendarPage } from '@/pages/CalendarPage';
import { FloorPage } from '@/pages/FloorPage';
import { GoogleCalendarCallbackPage } from '@/pages/GoogleCalendarCallbackPage';
import { MyPtoPage } from '@/pages/pto/MyPtoPage';
import { ManagePtoPage } from '@/pages/pto/ManagePtoPage';
import { HandbookPoliciesPage } from '@/pages/hr/HandbookPoliciesPage';
import { PersonalDocumentsPage } from '@/pages/hr/PersonalDocumentsPage';
import { OrgChartPage } from '@/pages/hr/OrgChartPage';
import { EmployeeReviewsPage } from '@/pages/hr/EmployeeReviewsPage';
import { ComplaintsPage } from '@/pages/hr/ComplaintsPage';
import { EmployeeInformationPage } from '@/pages/hr/EmployeeInformationPage';
import { EmployeeDetailPage } from '@/pages/hr/EmployeeDetailPage';
import { IntegrationsPage } from '@/pages/integrations/IntegrationsPage';
import { QboCallbackPage } from '@/pages/integrations/QboCallbackPage';
import { Ga4CallbackPage } from '@/pages/integrations/Ga4CallbackPage';
import { GoogleMarketingCallbackPage } from '@/pages/integrations/GoogleMarketingCallbackPage';
import { ShopifyCallbackPage } from '@/pages/integrations/ShopifyCallbackPage';
import { AmazonCallbackPage } from '@/pages/integrations/AmazonCallbackPage';
import { MarketingCallbackPage } from '@/pages/integrations/MarketingCallbackPage';
import { QuickbooksLaunchPage } from '@/pages/integrations/QuickbooksLaunchPage';
import { QuickbooksConnectPage } from '@/pages/integrations/QuickbooksConnectPage';
import { QuickbooksDisconnectPage } from '@/pages/integrations/QuickbooksDisconnectPage';
import { BankAccountsPage } from '@/pages/financials/BankAccountsPage';
import { TransactionsPage } from '@/pages/financials/TransactionsPage';
import { TransactionDetailPage } from '@/pages/financials/TransactionDetailPage';
import { QboHistoryDetailPage } from '@/pages/financials/transactions/QboHistoryDetailPage';
import { AmazonBusinessPage } from '@/pages/financials/AmazonBusinessPage';
import { ReconciliationPage } from '@/pages/financials/ReconciliationPage';
import { ExpenseTrackingPage } from '@/pages/financials/ExpenseTrackingPage';
import { ExpenseDetailPage } from '@/pages/financials/ExpenseDetailPage';
import { ProjectionsPage } from '@/pages/financials/projections/ProjectionsPage';
import { CapitalItemPage } from '@/pages/financials/projections/CapitalItemPage';
import { DebtBalancesPage } from '@/pages/financials/DebtBalancesPage';
import { LoansPage } from '@/pages/financials/loans/LoansPage';
import { FamilyLoanPage } from '@/pages/financials/loans/FamilyLoanPage';
import { SBALoanPage } from '@/pages/financials/loans/SBALoanPage';
import { RoleManagementPage } from '@/pages/users/RoleManagementPage';
import { CardMappingPage } from '@/pages/users/CardMappingPage';
import { AuditLogPage } from '@/pages/users/AuditLogPage';
// Phase 2 — Catalog, Inventory & Procurement
import { ItemsPage } from '@/pages/inventory/ItemsPage';
import { ItemDetailPage } from '@/pages/inventory/ItemDetailPage';
import { KindItemsPage } from '@/pages/inventory/KindItemsPage';
import { CustomersPage } from '@/pages/catalog/CustomersPage';
import { SuppliersPage } from '@/pages/catalog/SuppliersPage';
import { EquipmentPage } from '@/pages/catalog/EquipmentPage';
import { EquipmentCategoryPage } from '@/pages/catalog/EquipmentCategoryPage';
import { EquipmentDetailPage } from '@/pages/catalog/EquipmentDetailPage';
import { SoftwarePage } from '@/pages/catalog/SoftwarePage';
import { SoftwareDetailPage } from '@/pages/catalog/SoftwareDetailPage';
import { OnlineAccountsPage } from '@/pages/catalog/OnlineAccountsPage';
import { OnlineAccountDetailPage } from '@/pages/catalog/OnlineAccountDetailPage';
import { LocationsPage } from '@/pages/inventory/LocationsPage';
import { InventoryLevelsPage } from '@/pages/inventory/InventoryLevelsPage';
import { StockMovementsPage } from '@/pages/inventory/StockMovementsPage';
import { AdjustmentsPage } from '@/pages/inventory/AdjustmentsPage';
import { CycleCountPage } from '@/pages/inventory/CycleCountPage';
import { ProcurementCalendarPage } from '@/pages/procurement/ProcurementCalendarPage';
import { PurchaseOrdersPage } from '@/pages/procurement/PurchaseOrdersPage';
import { ReceivingPage } from '@/pages/procurement/ReceivingPage';
import { VendorInvoicesPage } from '@/pages/procurement/VendorInvoicesPage';
// Phase 3 — Manufacturing
import { ProductionPage } from '@/pages/manufacturing/ProductionPage';
import { EmployeeMatrixPage } from '@/pages/manufacturing/EmployeeMatrixPage';
import { UnitLogPage } from '@/pages/manufacturing/UnitLogPage';
import { ScrapLogPage } from '@/pages/manufacturing/ScrapLogPage';
import { BomsPage } from '@/pages/manufacturing/BomsPage';
import { BomDetailPage } from '@/pages/manufacturing/BomDetailPage';
import { WorkCentersPage } from '@/pages/manufacturing/WorkCentersPage';
import { WorkOrdersPage } from '@/pages/manufacturing/WorkOrdersPage';
import { ProductionPlanningPage } from '@/pages/manufacturing/ProductionPlanningPage';
import { QualityPage } from '@/pages/manufacturing/QualityPage';
import { WorkstationsPage } from '@/pages/manufacturing/WorkstationsPage';
import { WorkstationDetailPage } from '@/pages/manufacturing/WorkstationDetailPage';
import { WorkModePage } from '@/pages/work/WorkModePage';
// Phase 4 — Sales
import { OrdersPage } from '@/pages/sales/OrdersPage';
import { QuotesPage } from '@/pages/sales/QuotesPage';
import { ShipmentsPage } from '@/pages/sales/ShipmentsPage';
import { ReturnsPage } from '@/pages/sales/ReturnsPage';
import { CustomerFeedbackPage } from '@/pages/sales/CustomerFeedbackPage';
// Phase 5 — Quality / ISO
import { DocumentsPage } from '@/pages/iso/DocumentsPage';
import { TrainingPage } from '@/pages/iso/TrainingPage';
import { CalibrationPage } from '@/pages/iso/CalibrationPage';
import { SupplierQualificationPage } from '@/pages/iso/SupplierQualificationPage';
import { NcrCapaPage } from '@/pages/iso/NcrCapaPage';
import { AuditsPage } from '@/pages/iso/AuditsPage';
// Phase 6 — Intelligence
import { ForecastPage } from '@/pages/intelligence/ForecastPage';
import { KpisPage } from '@/pages/intelligence/KpisPage';
// Marketing — its own top-level section in the sidebar. Web analytics
// moved here from /intel/* alongside the new social / competitor /
// affiliate / advertising sub-pages so the whole marketing stack
// (paid, organic, partner) lives under one heading.
import { SalesMarketingDashboardPage } from '@/pages/marketing/SalesMarketingDashboardPage';
import { MarketingDashboardPage } from '@/pages/marketing/MarketingDashboardPage';
import { WebAnalyticsPage } from '@/pages/marketing/WebAnalyticsPage';
import { SocialMediaPage } from '@/pages/marketing/SocialMediaPage';
import { CompetitorsPage } from '@/pages/marketing/CompetitorsPage';
import { AffiliatesPage } from '@/pages/marketing/AffiliatesPage';
import { AdvertisingPage } from '@/pages/marketing/AdvertisingPage';
// Phase 7 — Tasks (ticket inbox + task lists / to-dos, grouped by department)
import { TicketsPage } from '@/pages/tickets/TicketsPage';
import { MyTasksPage } from '@/pages/projects/MyTasksPage';
import { ProjectDetailPage } from '@/pages/projects/ProjectDetailPage';
// Engineering (migration 142) — projects, CAD file drive, 3D prints, costs
import { EngineeringDashboardPage } from '@/pages/engineering/EngineeringDashboardPage';
import { EngineeringProjectPage } from '@/pages/engineering/EngineeringProjectPage';
import { EngineeringPrintsPage } from '@/pages/engineering/EngineeringPrintsPage';
// Company Admin — shared templates and reference info. Not the locked
// Settings surfaces (roles, integrations, audit).
import { TemplatesPage } from '@/pages/admin/TemplatesPage';
import { CompanyProfilePage } from '@/pages/admin/CompanyProfilePage';
import { LicensesPage } from '@/pages/admin/LicensesPage';
import { ContractsPage } from '@/pages/admin/ContractsPage';
import { ContactsPage } from '@/pages/admin/ContactsPage';
import { BrandKitPage } from '@/pages/admin/BrandKitPage';

// Legacy `/inventory/items/:id` URLs land here. We forward to the new
// canonical `/catalog/items/:id` location so old bookmarks keep working
// after the Items page migrated under the Catalog section.
function LegacyItemRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/catalog/items/${id ?? ''}`} replace />;
}

// Software has two sub-pages. Accounts are shared one by one, so
// someone without the Catalog section can still have accounts and lands
// there instead of on Subscriptions.
function SoftwareIndexRedirect() {
  const { profile } = useAuth();
  const to = canRead(profile?.role ?? null, 'catalog', profile?.section_access)
    ? '/catalog/software/subscriptions'
    : '/catalog/software/accounts';
  return <Navigate to={to} replace />;
}

function LegacySoftwareRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/catalog/software/subscriptions/${id ?? ''}`} replace />;
}

// The standalone Actuals page merged into Main Financials; its tab ids
// (pnl / balance-sheet / cashflow / ar-ap) exist there too, so forward the
// bookmark to the same tab.
function ActualsRedirect() {
  const [params] = useSearchParams();
  const tab = params.get('tab');
  const to = tab ? `/financials/projections?tab=${encodeURIComponent(tab)}` : '/financials/projections?tab=pnl';
  return <Navigate to={to} replace />;
}

// RLS decides which accounts each person sees; lenders never do.
const ONLINE_ACCOUNT_ROLES: Role[] = ['super_admin', 'admin', 'manager', 'employee'];


export default function App() {
  return (
    <TooltipProvider delayDuration={200}>
      <Routes>
        {/* Public auth routes */}
        <Route path="/auth/sign-in" element={<SignInPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/auth/pending" element={<PendingPage />} />
        <Route path="/auth/inactive" element={<InactivePage />} />
        <Route path="/forbidden" element={<ForbiddenPage />} />

        {/* Public homepage. Must stay unauthenticated so Google OAuth brand
            verification (and other external reviewers) can read the Atlas
            name and purpose without signing in. Signed-in users are
            forwarded into the app by HomePage itself. */}
        <Route path="/" element={<HomePage />} />

        {/* Public legal pages — must remain reachable without auth so external
            reviewers (e.g. Intuit / QBO production-app onboarding) can fetch
            them at a stable URL. */}
        <Route path="/legal/eula" element={<EulaPage />} />
        <Route path="/eula" element={<Navigate to="/legal/eula" replace />} />
        <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />

        {/* Public QuickBooks Online lifecycle URLs. These are the URLs
            registered with Intuit on the App Settings page (Launch URL /
            Connect URL / Disconnect URL) and must be reachable without
            authentication so Intuit's reviewers and Intuit-redirected users
            can land on them. They render the public Atlas chrome and route
            the user into the authenticated app when appropriate.
            `/intuit/*` aliases are kept so either form works in the Intuit
            developer dashboard. */}
        <Route path="/quickbooks/launch" element={<QuickbooksLaunchPage />} />
        <Route path="/intuit/launch" element={<Navigate to="/quickbooks/launch" replace />} />
        <Route path="/quickbooks/connect" element={<QuickbooksConnectPage />} />
        <Route path="/intuit/connect" element={<Navigate to="/quickbooks/connect" replace />} />
        <Route path="/quickbooks/disconnect" element={<QuickbooksDisconnectPage />} />
        <Route path="/intuit/disconnect" element={<Navigate to="/quickbooks/disconnect" replace />} />

        {/* Click-through EULA acceptance gate. Authenticated but NOT wrapped
            in RequireEulaAcceptance — it's the page the gate redirects TO,
            so wrapping it in the gate would cause an infinite loop. */}
        <Route
          path="/legal/eula/accept"
          element={
            <RequireAuth>
              <EulaAcceptPage />
            </RequireAuth>
          }
        />

        {/* MFA enrollment + challenge — authenticated but NOT wrapped in
            RequireMfa (these are the pages RequireMfa redirects TO, so
            wrapping would loop). They ARE wrapped in RequireAuth so an
            anonymous attacker can't enroll a factor on someone else's
            session. */}
        <Route
          path="/auth/mfa/setup"
          element={
            <RequireAuth>
              <MfaSetupPage />
            </RequireAuth>
          }
        />
        <Route
          path="/auth/mfa/challenge"
          element={
            <RequireAuth>
              <MfaChallengePage />
            </RequireAuth>
          }
        />

        {/* Work Mode — the full-screen station tablet UI (migration 116).
            Deliberately OUTSIDE the AppShell: no sidebar, no topbar, big
            touch targets. The tablet is signed in as one Atlas account;
            workers identify themselves with a PIN / badge inside the page.
            Gated on the role-only `work_mode` flag (every org member, no
            external lenders) rather than `manufacturing`, so an employee
            whose Manufacturing section has been switched off can still
            sign in on a station tablet. */}
        <Route
          path="/work"
          element={
            <RequireAuth module="work_mode">
              <RequireEulaAcceptance>
                <WorkModePage />
              </RequireEulaAcceptance>
            </RequireAuth>
          }
        />

        {/* Authenticated app shell — auth + current EULA acceptance required.
            RequireEulaAcceptance sits inside RequireAuth so it only runs
            once we know the user has a valid profile. */}
        <Route
          element={
            <RequireAuth>
              <RequireEulaAcceptance>
                <AppShell />
              </RequireEulaAcceptance>
            </RequireAuth>
          }
        >
          {/* Dashboard is gated by the `dashboard` module flag so the
              external_lender role (no dashboard access) bounces to
              /forbidden instead of seeing a page full of widgets they
              can't populate. Every other role has dashboard:true so
              this is a no-op for them. */}
          <Route path="/dashboard" element={<RequireAuth module="dashboard"><DashboardPage /></RequireAuth>} />
          {/* Team calendar — shared org surface (migration 80). Gated on
              the `calendar` module flag so external_lender stays out;
              every other role can read + write. */}
          <Route path="/calendar" element={<RequireAuth module="calendar"><CalendarPage /></RequireAuth>} />
          {/* Per-user Google Calendar OAuth redirect (migration 83). Gated
              on the same `calendar` module flag as the page that starts
              the flow — NOT super_admin, since any team member may link
              their own Google account. */}
          <Route path="/calendar/google/callback" element={<RequireAuth module="calendar"><GoogleCalendarCallbackPage /></RequireAuth>} />
          {/* Digital Company — Sims-style virtual 230 Stanford Parkway
              (migration 111; renamed from Digital Office → The Floor →
              Digital Company). Super admins edit the building layout
              (migration 120). Gated on the `digital_office` module flag.
              /floor and /digital-office keep working as aliases. */}
          <Route path="/digital-company" element={<RequireAuth module="digital_office"><FloorPage /></RequireAuth>} />
          <Route path="/floor" element={<RequireAuth module="digital_office"><FloorPage /></RequireAuth>} />
          <Route path="/digital-office" element={<Navigate to="/digital-company" replace />} />

          {/* HR — people-ops (PTO, handbook, personal docs). Payroll stays
              in Gusto. PTO tracking is migration 95: "My PTO" is the
              per-person self-service surface; "Manage PTO" is the
              approver surface (super_admin / admin / manager). The DB
              enforces the same split via RLS + pto_can_approve() /
              pto_can_manage_awards(). Legacy /pto URLs redirect below. */}
          <Route path="/hr/pto" element={<RequireAuth module="pto"><MyPtoPage /></RequireAuth>} />
          <Route path="/hr/pto/manage" element={<RequireAuth roles={['super_admin', 'admin', 'manager']}><ManagePtoPage /></RequireAuth>} />
          <Route path="/hr/employees" element={<RequireAuth roles={['super_admin', 'admin', 'manager']}><EmployeeInformationPage /></RequireAuth>} />
          <Route path="/hr/employees/:profileId" element={<RequireAuth roles={['super_admin', 'admin', 'manager']}><EmployeeDetailPage /></RequireAuth>} />
          <Route path="/hr/org-chart" element={<RequireAuth module="hr"><OrgChartPage /></RequireAuth>} />
          <Route path="/hr/reviews" element={<RequireAuth module="hr"><EmployeeReviewsPage /></RequireAuth>} />
          <Route path="/hr/handbook" element={<RequireAuth module="hr"><HandbookPoliciesPage /></RequireAuth>} />
          <Route path="/hr/documents" element={<RequireAuth module="hr"><PersonalDocumentsPage /></RequireAuth>} />
          <Route path="/hr/complaints" element={<RequireAuth module="hr"><ComplaintsPage /></RequireAuth>} />
          <Route path="/pto" element={<Navigate to="/hr/pto" replace />} />
          <Route path="/pto/manage" element={<Navigate to="/hr/pto/manage" replace />} />

          {/* Notifications inbox — @mention alerts. Reachable by every
              authenticated user (anyone can be tagged); per-item access
              is enforced by RLS on the notifications table. */}
          <Route path="/notifications" element={<NotificationsPage />} />

          {/* Account-level pages reachable by every authenticated user. */}
          <Route path="/account/settings" element={<SettingsPage />} />
          <Route path="/account/security" element={<MfaSettingsPage />} />

          {/* Financials. All financials surfaces are double-gated:
              RequireAuth checks role, RequireMfa checks AAL2 (the DB
              enforces the same via RLS in migration 23). Expense tracking
              is the one exception — it's per-user receipts, not the
              org's books, so it stays at AAL1. */}
          <Route path="/financials/expenses" element={<RequireAuth module="expenses"><ExpenseTrackingPage /></RequireAuth>} />
          <Route path="/financials/expenses/:id" element={<RequireAuth module="expenses"><ExpenseDetailPage /></RequireAuth>} />
          <Route path="/financials/bank-accounts" element={<RequireAuth module="financials"><RequireMfa><BankAccountsPage /></RequireMfa></RequireAuth>} />
          <Route path="/financials/transactions" element={<RequireAuth module="financials"><RequireMfa><TransactionsPage /></RequireMfa></RequireAuth>} />
          {/* More-specific history detail route declared before the
              generic `:id` route so React Router's ranking can't be
              fooled by future param-only refactors. */}
          <Route path="/financials/transactions/history/:id" element={<RequireAuth module="financials"><RequireMfa><QboHistoryDetailPage /></RequireMfa></RequireAuth>} />
          <Route path="/financials/transactions/:id" element={<RequireAuth module="financials"><RequireMfa><TransactionDetailPage /></RequireMfa></RequireAuth>} />
          <Route path="/financials/amazon-business" element={<RequireAuth module="financials"><RequireMfa><AmazonBusinessPage /></RequireMfa></RequireAuth>} />
          <Route path="/financials/reconciliation" element={<RequireAuth module="financials"><RequireMfa><ReconciliationPage /></RequireMfa></RequireAuth>} />
          <Route path="/financials/projections" element={<RequireAuth module="financials"><RequireMfa><ProjectionsPage /></RequireMfa></RequireAuth>} />
          {/* Capital item detail (new + edit) — linked from the Projections
              Capital Planning tab. `/new` declared via the same param route;
              the page treats id==='new' as create mode. */}
          <Route path="/financials/projections/capital/:id" element={<RequireAuth module="financials"><RequireMfa><CapitalItemPage /></RequireMfa></RequireAuth>} />
          {/* Loans — sub-section of Financials gated by the dedicated
              `loans` module flag (NOT `financials`) so an
              `external_lender` can be granted visibility into a
              specific loan without unlocking bank balances, P&L, or
              anything else under financials. The Loans overview and
              Family loan pages allow both super_admin and
              external_lender; SBA is super_admin-only via explicit
              `roles=['super_admin']` because external lenders
              shouldn't see SBA financing terms.
              MFA wrap is kept for parity with the other financials
              surfaces — it's a no-op today (MFA_LOCKOUT_ENABLED=false)
              and would only fire if/when we re-enable the lockout. */}
          {/* Debt balances rolls up every loan + credit line, including
              bank balances, so it's gated on `financials` (super_admin)
              rather than the lender-visible `loans` module. */}
          <Route path="/financials/debt-balances" element={<RequireAuth module="financials"><RequireMfa><DebtBalancesPage /></RequireMfa></RequireAuth>} />
          <Route path="/financials/loans" element={<RequireAuth module="loans"><RequireMfa><LoansPage /></RequireMfa></RequireAuth>} />
          <Route path="/financials/loans/family" element={<RequireAuth module="loans"><RequireMfa><FamilyLoanPage /></RequireMfa></RequireAuth>} />
          <Route path="/financials/loans/sba" element={<RequireAuth roles={['super_admin']}><RequireMfa><SBALoanPage /></RequireMfa></RequireAuth>} />
          {/* Legacy direct-link URLs — the standalone Actuals page was folded
              into Main Financials (same tab ids), so old bookmarks land on
              the matching tab there. */}
          <Route path="/financials/actuals" element={<ActualsRedirect />} />
          <Route path="/financials/profit-and-loss" element={<Navigate to="/financials/projections?tab=pnl" replace />} />
          <Route path="/financials/balance-sheet" element={<Navigate to="/financials/projections?tab=balance-sheet" replace />} />
          <Route path="/financials/cash-flow" element={<Navigate to="/financials/projections?tab=cashflow" replace />} />
          <Route path="/financials/ar-ap" element={<Navigate to="/financials/projections?tab=ar-ap" replace />} />

          {/* Catalog, Inventory & Procurement (Phase 2). Items live under
              /catalog/* now that Items / Customers / Suppliers share a
              "master record" section in the sidebar. /inventory/items is
              kept as a redirect so existing bookmarks and deep links keep
              working. */}
          <Route path="/catalog/items" element={<RequireAuth module="catalog"><ItemsPage /></RequireAuth>} />
          {/* Per-kind variant — listed BEFORE `:id` so React Router
              matches the static `kind` segment ahead of the dynamic
              one. `KindItemsPage` resolves the slug and delegates to
              `ItemsPage` with the kind filter applied. */}
          <Route path="/catalog/items/kind/:kindSlug" element={<RequireAuth module="catalog"><KindItemsPage /></RequireAuth>} />
          <Route path="/catalog/items/:id" element={<RequireAuth module="catalog"><ItemDetailPage /></RequireAuth>} />
          <Route path="/catalog/equipment" element={<RequireAuth module="catalog"><EquipmentPage /></RequireAuth>} />
          <Route path="/catalog/equipment/category/:category" element={<RequireAuth module="catalog"><EquipmentCategoryPage /></RequireAuth>} />
          <Route path="/catalog/equipment/:id" element={<RequireAuth module="catalog"><EquipmentDetailPage /></RequireAuth>} />
          <Route path="/catalog/customers" element={<RequireAuth module="sales"><CustomersPage /></RequireAuth>} />
          <Route path="/catalog/suppliers" element={<RequireAuth module="procurement"><SuppliersPage /></RequireAuth>} />
          {/* Software: Subscriptions (migration 133) are readable with the
              Catalog section and RLS keeps writes to super_admin / admin.
              Accounts (migration 140) are closed by default and shared per
              person, so they are not tied to the Catalog section. */}
          <Route path="/catalog/software" element={<RequireAuth roles={ONLINE_ACCOUNT_ROLES}><SoftwareIndexRedirect /></RequireAuth>} />
          <Route path="/catalog/software/subscriptions" element={<RequireAuth module="catalog"><SoftwarePage /></RequireAuth>} />
          <Route path="/catalog/software/subscriptions/:id" element={<RequireAuth module="catalog"><SoftwareDetailPage /></RequireAuth>} />
          <Route path="/catalog/software/accounts" element={<RequireAuth roles={ONLINE_ACCOUNT_ROLES}><OnlineAccountsPage /></RequireAuth>} />
          <Route path="/catalog/software/accounts/:id" element={<RequireAuth roles={ONLINE_ACCOUNT_ROLES}><OnlineAccountDetailPage /></RequireAuth>} />
          <Route path="/catalog/software/:id" element={<LegacySoftwareRedirect />} />
          <Route path="/inventory/items" element={<Navigate to="/catalog/items" replace />} />
          <Route path="/inventory/items/:id" element={<LegacyItemRedirect />} />
          <Route path="/inventory/locations" element={<RequireAuth module="inventory"><LocationsPage /></RequireAuth>} />
          <Route path="/inventory/levels" element={<RequireAuth module="inventory"><InventoryLevelsPage /></RequireAuth>} />
          <Route path="/inventory/movements" element={<RequireAuth module="inventory"><StockMovementsPage /></RequireAuth>} />
          <Route path="/inventory/adjustments" element={<RequireAuth module="inventory"><AdjustmentsPage /></RequireAuth>} />
          <Route path="/inventory/cycle-count" element={<RequireAuth module="inventory"><CycleCountPage /></RequireAuth>} />
          {/* Buying side of production planning: vendor orders by day with one-click draft POs. */}
          <Route path="/procurement/calendar" element={<RequireAuth module="procurement"><ProcurementCalendarPage /></RequireAuth>} />
          <Route path="/procurement/purchase-orders" element={<RequireAuth module="procurement"><PurchaseOrdersPage /></RequireAuth>} />
          <Route path="/procurement/receiving" element={<RequireAuth module="procurement"><ReceivingPage /></RequireAuth>} />
          <Route path="/procurement/goods-receipts" element={<Navigate to="/procurement/receiving" replace />} />
          <Route path="/procurement/vendor-invoices" element={<RequireAuth module="procurement"><VendorInvoicesPage /></RequireAuth>} />

          {/* Manufacturing (Phase 3) */}
          <Route path="/mfg/production" element={<RequireAuth module="manufacturing"><ProductionPage /></RequireAuth>} />
          <Route path="/mfg/production/employee-matrix" element={<RequireAuth module="manufacturing"><EmployeeMatrixPage /></RequireAuth>} />
          <Route path="/mfg/production/units" element={<RequireAuth module="manufacturing"><UnitLogPage /></RequireAuth>} />
          <Route path="/mfg/scrap-log" element={<RequireAuth module="manufacturing"><ScrapLogPage /></RequireAuth>} />
          {/* Workstations — the manager side of Work Mode: live stations,
              people (PIN / badge), defects, feedback, instructions,
              morale, history. */}
          <Route path="/mfg/workstations" element={<RequireAuth module="manufacturing"><WorkstationsPage /></RequireAuth>} />
          <Route path="/mfg/workstations/:stationId" element={<RequireAuth module="manufacturing"><WorkstationDetailPage /></RequireAuth>} />
          <Route path="/mfg/boms" element={<RequireAuth module="manufacturing"><BomsPage /></RequireAuth>} />
          <Route path="/mfg/boms/:itemId" element={<RequireAuth module="manufacturing"><BomDetailPage /></RequireAuth>} />
          <Route path="/mfg/work-centers" element={<Navigate to="/mfg/work-schedules" replace />} />
          <Route path="/mfg/work-schedules" element={<RequireAuth module="manufacturing"><WorkCentersPage /></RequireAuth>} />
          <Route path="/mfg/work-orders" element={<RequireAuth module="manufacturing"><WorkOrdersPage /></RequireAuth>} />
          {/* Production planning + procurement calendar (migration 150).
              Replaces the old MRP stub; /mfg/mrp stays as a redirect. */}
          <Route path="/mfg/planning" element={<RequireAuth module="manufacturing"><ProductionPlanningPage /></RequireAuth>} />
          <Route path="/mfg/mrp" element={<Navigate to="/mfg/planning" replace />} />
          <Route path="/mfg/quality" element={<RequireAuth module="manufacturing"><QualityPage /></RequireAuth>} />

          {/* Sales (Phase 4) */}
          <Route path="/sales/orders" element={<RequireAuth module="sales"><OrdersPage /></RequireAuth>} />
          <Route path="/sales/quotes" element={<RequireAuth module="sales"><QuotesPage /></RequireAuth>} />
          <Route path="/sales/shipments" element={<RequireAuth module="sales"><ShipmentsPage /></RequireAuth>} />
          <Route path="/sales/returns" element={<RequireAuth module="sales"><ReturnsPage /></RequireAuth>} />
          <Route path="/sales/feedback" element={<RequireAuth module="sales"><CustomerFeedbackPage /></RequireAuth>} />

          {/* ISO / Quality (Phase 5) */}
          <Route path="/iso/documents" element={<RequireAuth module="sops"><DocumentsPage /></RequireAuth>} />
          <Route path="/iso/training" element={<RequireAuth module="sops"><TrainingPage /></RequireAuth>} />
          <Route path="/iso/calibration" element={<RequireAuth module="sops"><CalibrationPage /></RequireAuth>} />
          <Route path="/iso/suppliers" element={<RequireAuth module="sops"><SupplierQualificationPage /></RequireAuth>} />
          <Route path="/iso/ncr-capa" element={<RequireAuth module="sops"><NcrCapaPage /></RequireAuth>} />
          <Route path="/iso/audits" element={<RequireAuth module="sops"><AuditsPage /></RequireAuth>} />

          {/* Intelligence (Phase 6) */}
          <Route path="/intel/forecast" element={<RequireAuth module="intelligence"><ForecastPage /></RequireAuth>} />
          <Route path="/intel/kpis" element={<RequireAuth module="intelligence"><KpisPage /></RequireAuth>} />

          {/* Marketing — promoted to its own top-level section. We
              still gate everything on the `intelligence` module read
              flag because Atlas has no separate `marketing` role
              dimension yet; if/when we add one, swap the module key
              in one place. */}
          <Route path="/marketing/sales-marketing" element={<RequireAuth module="marketing"><SalesMarketingDashboardPage /></RequireAuth>} />
          <Route path="/marketing/dashboard" element={<RequireAuth module="marketing"><MarketingDashboardPage /></RequireAuth>} />
          <Route path="/marketing/web-analytics" element={<RequireAuth module="marketing"><WebAnalyticsPage /></RequireAuth>} />
          <Route path="/marketing/social-media" element={<RequireAuth module="marketing"><SocialMediaPage /></RequireAuth>} />
          <Route path="/marketing/competitors" element={<RequireAuth module="marketing"><CompetitorsPage /></RequireAuth>} />
          <Route path="/marketing/affiliates" element={<RequireAuth module="marketing"><AffiliatesPage /></RequireAuth>} />
          <Route path="/marketing/advertising" element={<RequireAuth module="marketing"><AdvertisingPage /></RequireAuth>} />

          {/* Legacy /intel/* URLs — kept as redirects so existing
              bookmarks and command-palette muscle memory still land
              on the right new home. */}
          <Route path="/intel/marketing" element={<Navigate to="/marketing/dashboard" replace />} />
          <Route path="/intel/web-analytics" element={<Navigate to="/marketing/web-analytics" replace />} />

          {/* Tasks (Phase 7). /tasks is the cross-department hub — the
              signed-in user's open to-dos plus every task list they're a
              member of, filterable by department (?department=). Task
              lists keep their department-agnostic /projects/:id URL so
              notifications, calendar links, and future department pages
              all point at the same place. Module read is open to every
              role; per-project visibility is then enforced by RLS via
              project_members. The detail page handles its own
              "you don't have access" empty state if the user follows a
              direct link to a project they aren't a member of. */}
          {/* Tasks → Ticket inbox (migration 143). Role-only module, so it
              stays reachable even when the Tasks section is revoked. */}
          <Route path="/tickets" element={<RequireAuth module="tickets"><TicketsPage /></RequireAuth>} />
          <Route path="/tickets/:id" element={<RequireAuth module="tickets"><TicketsPage /></RequireAuth>} />
          <Route path="/tasks" element={<RequireAuth module="projects"><MyTasksPage /></RequireAuth>} />
          <Route path="/projects/:id" element={<RequireAuth module="projects"><ProjectDetailPage /></RequireAuth>} />
          {/* Legacy dashboard URL from when this section was called
              "Project Management". */}
          <Route path="/projects" element={<Navigate to="/tasks" replace />} />

          {/* Engineering (migration 142). Readable by everyone with the
              Engineering section so production can reach a part's files;
              writes are limited to each project's team by RLS. The print
              log route comes before /:id so it isn't swallowed by it. */}
          <Route path="/engineering" element={<RequireAuth module="engineering"><EngineeringDashboardPage /></RequireAuth>} />
          <Route path="/engineering/prints" element={<RequireAuth module="engineering"><EngineeringPrintsPage /></RequireAuth>} />
          <Route path="/engineering/:id" element={<RequireAuth module="engineering"><EngineeringProjectPage /></RequireAuth>} />

          {/* Company Admin (migration 121). Readable by every org member
              except external lenders; writes are super_admin / admin.
              Card mapping stays at /admin/card-mapping under the locked
              Settings section. */}
          <Route path="/admin/templates" element={<RequireAuth module="company_admin"><TemplatesPage /></RequireAuth>} />
          <Route path="/admin/company" element={<RequireAuth module="company_admin"><CompanyProfilePage /></RequireAuth>} />
          <Route path="/admin/licenses" element={<RequireAuth module="company_admin"><LicensesPage /></RequireAuth>} />
          <Route path="/admin/contracts" element={<RequireAuth module="company_admin"><ContractsPage /></RequireAuth>} />
          <Route path="/admin/contacts" element={<RequireAuth module="company_admin"><ContactsPage /></RequireAuth>} />
          <Route path="/admin/brand" element={<RequireAuth module="company_admin"><BrandKitPage /></RequireAuth>} />

          {/* Integrations. Super admin by default; grantable per person
              from Roles & users without opening Roles & users / audit.
              MFA-gated alongside financials because the page can also
              connect Mercury / QBO / Plaid. */}
          <Route path="/integrations" element={<RequireAuth module="integrations"><RequireMfa><IntegrationsPage /></RequireMfa></RequireAuth>} />
          <Route path="/integrations/qbo/callback" element={<RequireAuth module="integrations"><RequireMfa><QboCallbackPage /></RequireMfa></RequireAuth>} />
          <Route path="/integrations/ga4/callback" element={<RequireAuth module="integrations"><RequireMfa><Ga4CallbackPage /></RequireMfa></RequireAuth>} />
          <Route path="/integrations/shopify/callback" element={<RequireAuth module="integrations"><RequireMfa><ShopifyCallbackPage /></RequireMfa></RequireAuth>} />
          <Route path="/integrations/amazon/callback" element={<RequireAuth module="integrations"><RequireMfa><AmazonCallbackPage /></RequireMfa></RequireAuth>} />

          {/* Marketing OAuth callbacks (migration 57). All six share the
              MarketingCallbackPage shell — only `provider` differs. The
              dashed routes match the redirect URIs surfaced by
              `marketingRedirectUri()` in the matching OAuth helper. */}
          <Route path="/integrations/amazon-ads/callback" element={<RequireAuth module="integrations"><RequireMfa><MarketingCallbackPage provider="amazon_ads" /></RequireMfa></RequireAuth>} />
          <Route path="/integrations/google-ads/callback" element={<RequireAuth module="integrations"><RequireMfa><MarketingCallbackPage provider="google_ads" /></RequireMfa></RequireAuth>} />
          <Route path="/integrations/meta-ads/callback" element={<RequireAuth module="integrations"><RequireMfa><MarketingCallbackPage provider="meta_ads" /></RequireMfa></RequireAuth>} />
          <Route path="/integrations/facebook/callback" element={<RequireAuth module="integrations"><RequireMfa><MarketingCallbackPage provider="facebook" /></RequireMfa></RequireAuth>} />
          <Route path="/integrations/instagram/callback" element={<RequireAuth module="integrations"><RequireMfa><MarketingCallbackPage provider="instagram" /></RequireMfa></RequireAuth>} />
          <Route path="/integrations/tiktok/callback" element={<RequireAuth module="integrations"><RequireMfa><MarketingCallbackPage provider="tiktok" /></RequireMfa></RequireAuth>} />

          {/* Search Console + YouTube (migration 101). Org-scoped Google
              OAuth on the same Cloud client as GA4, so they share a
              callback shell that differs only by which Edge Functions it
              calls and what resource the operator picks. */}
          <Route path="/integrations/search-console/callback" element={<RequireAuth module="integrations"><RequireMfa><GoogleMarketingCallbackPage provider="search_console" /></RequireMfa></RequireAuth>} />
          <Route path="/integrations/youtube/callback" element={<RequireAuth module="integrations"><RequireMfa><GoogleMarketingCallbackPage provider="youtube" /></RequireMfa></RequireAuth>} />

          {/* User & role management (super_admin only). Card mapping
              exposes which employee owns which Mercury card, so we treat
              it as a financials-adjacent surface and require MFA. Role
              management itself doesn't leak financials data, so it stays
              at AAL1 — admins still need to be able to log in and
              demote a compromised super_admin even if they can't pass
              MFA themselves. */}
          <Route path="/users" element={<RequireAuth roles={['super_admin']}><RoleManagementPage /></RequireAuth>} />
          <Route path="/admin/card-mapping" element={<RequireAuth roles={['super_admin']}><RequireMfa><CardMappingPage /></RequireMfa></RequireAuth>} />
          <Route path="/audit" element={<RequireAuth module="audit"><RequireMfa><AuditLogPage /></RequireMfa></RequireAuth>} />

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </TooltipProvider>
  );
}
